/**
 * The contract between the web layer and the Capacitor shell (app-shell/).
 *
 * The shell injects `window.InceptionAINative` before the first paint. Every
 * web consumer follows the same shape:
 *
 *   const bridge = getBridge();
 *   if (bridge) { native path } else { existing web path }
 *
 * so nothing regresses in a normal browser. New bridge methods are optional
 * at the type level until the minimum shell version that implements them is
 * enforced — see min-shell-version.ts. The web deploys continuously; old
 * shells stay in the field for months.
 */

export type ShellSignInResult = {
  /** OIDC id token from the native provider (Google / Apple). */
  idToken: string;
  /** Raw nonce for Sign in with Apple's credential exchange. */
  rawNonce?: string;
};

export type NativeBridge = {
  /** Shell semver, e.g. "1.0.3". */
  version: string;
  platform: 'ios' | 'android';
  /**
   * Open a URL OUTSIDE the webview, in the system browser. Amazon/Audible
   * must leave the app (the Associates Mobile Application Policy prohibits
   * rendering Amazon in an app's own browser); every other host follows the
   * same rule, so the app never renders an arbitrary web page in a surface
   * it controls — which is also what keeps the App Store age rating's
   * "Unrestricted Web Access" answer a defensible No. https: URLs only.
   */
  openExternal(url: string): Promise<void>;
  /** Native share sheet. */
  share(payload: { title?: string; text?: string; url: string }): Promise<void>;
  /**
   * Native camera / photo-library picker. Returns a downscaled JPEG data URL
   * (max dimension 1568, matching lib/image-attachment.ts) so the web layer
   * never hits the HEIC decode path or the 3MB encoded cap.
   */
  pickImage(opts: { source: 'camera' | 'library' }): Promise<{ dataUrl: string } | null>;
  /** Ask for push permission + FCM/APNs token. Null when denied. */
  requestPush(): Promise<{ token: string } | null>;
  /** Native sign-in; the web completes it via signInWithCredential. */
  signIn(provider: 'google' | 'apple'): Promise<ShellSignInResult>;
  /**
   * Mint a fresh Apple authorization code so the web layer can REVOKE the
   * Sign in with Apple token at account deletion — required by App Store
   * 5.1.1(v), and not satisfiable from a stored token because Firebase does
   * not expose Apple's refresh token. Resolves null when unavailable
   * (Android, older shells, or the user dismissing the re-auth sheet).
   */
  refreshAppleAuthorization?(): Promise<string | null>;
  /** Web signals first meaningful paint; the shell hides the native splash. */
  appReady(): void;
  haptic(style: 'light' | 'medium' | 'success'): void;
  /** Device-local saved shelf (readable offline). */
  saveItem(item: { id: string; kind: 'product' | 'guide'; url: string; title: string; imageUrl?: string }): Promise<void>;
  removeSavedItem(id: string): Promise<void>;
  listSaved(): Promise<Array<{ id: string; kind: 'product' | 'guide'; url: string; title: string; imageUrl?: string }>>;
};

declare global {
  interface Window {
    InceptionAINative?: NativeBridge;
    /** Injected by the Capacitor runtime when running inside the shell. */
    Capacitor?: {
      getPlatform?: () => string;
      Plugins?: Record<string, any>;
    };
  }
}

/** Null in a normal browser — callers must always keep the web fallback. */
export function getBridge(): NativeBridge | null {
  if (typeof window === 'undefined') return null;
  if (window.InceptionAINative) return window.InceptionAINative;
  // Lazily assemble the bridge from the Capacitor runtime the shell injects
  // (server.url mode injects capacitor.js + plugin proxies into this origin).
  // Built on first call so consumer mount order never matters.
  const adapter = buildCapacitorAdapter();
  if (adapter) {
    window.InceptionAINative = adapter;
  }
  return adapter;
}

function buildCapacitorAdapter(): NativeBridge | null {
  const cap = window.Capacitor;
  const plugins = cap?.Plugins;
  const platform = cap?.getPlatform?.();
  if (!plugins || (platform !== 'ios' && platform !== 'android')) return null;

  const custom = plugins.InceptionAIBridge;
  const { version } = parseShellVersionFromUa();

  return {
    version,
    platform,
    async openExternal(url) {
      await custom?.openExternal({ url });
    },
    async share(payload) {
      await plugins.Share?.share(payload);
    },
    async pickImage(opts) {
      const photo = await plugins.Camera?.getPhoto({
        source: opts.source === 'camera' ? 'CAMERA' : 'PHOTOS',
        resultType: 'dataUrl',
        // Matches lib/image-attachment.ts MAX_DIMENSION — bypasses the HEIC
        // decode path and the 3MB encoded cap entirely.
        width: 1568,
        quality: 85,
        correctOrientation: true,
      });
      return photo?.dataUrl ? { dataUrl: photo.dataUrl } : null;
    },
    async requestPush() {
      const perm = await plugins.PushNotifications?.requestPermissions();
      if (perm?.receive !== 'granted') return null;
      return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), 10_000);
        plugins.PushNotifications?.addListener('registration', (token: { value: string }) => {
          clearTimeout(timer);
          resolve({ token: token.value });
        });
        plugins.PushNotifications?.addListener('registrationError', () => {
          clearTimeout(timer);
          resolve(null);
        });
        plugins.PushNotifications?.register();
      });
    },
    async refreshAppleAuthorization() {
      try {
        const result = await custom?.refreshAppleAuthorization();
        return result?.authorizationCode ?? null;
      } catch {
        return null;
      }
    },
    async signIn(provider) {
      if (provider === 'apple') {
        const result = await custom?.signInApple();
        if (!result?.idToken) throw new Error('apple_signin_failed');
        return { idToken: result.idToken, rawNonce: result.rawNonce };
      }
      // Native Google sign-in lands with the Phase-4 Credential Manager work.
      throw new Error('google_native_signin_not_available');
    },
    appReady() {
      void custom?.appReady();
      // Belt: hide via the stock plugin too (launchAutoHide=false holds it).
      void plugins.SplashScreen?.hide();
    },
    haptic(style) {
      void plugins.Haptics?.impact({ style: style === 'success' ? 'MEDIUM' : 'LIGHT' });
    },
    async saveItem(item) {
      await custom?.saveItem(item);
    },
    async removeSavedItem(id) {
      await custom?.removeSavedItem({ id });
    },
    async listSaved() {
      const result = await custom?.listSaved();
      return result?.items ?? [];
    },
  };
}

function parseShellVersionFromUa(): { version: string } {
  const match = navigator.userAgent.match(/InceptionAIApp\/(\d+(?:\.\d+){0,2})/i);
  return { version: match?.[1] ?? '1.0.0' };
}
